import {
  Conversation,
  Message,
  MetaConversation,
} from 'lib-shared-legacy-types';
import type { ReadConversationApiResponse } from 'lib-shared-types-api';
import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { conversationsApi } from 'apis/conversations';
import { getMessagesTranscript } from 'assets/app/utils/messages';
import { getTranscriptLead } from 'assets/app/utils/transcriptLead';
import type { EmailMessageType } from 'assets/components/conversation/reducers/emailMessage';
import { useAppSelector } from 'utils/redux/useAppSelector';

type LinkedData = {
  body: string;
  subject: string;
  forwardBody: string;
  forwardSubject: string;
  attachments: {
    url: string;
    prettyName: string;
    absoluteUri: string;
    size: number;
  }[];
};

export type LinkedConversationData = {
  /** Builds the parent's transcript on first call, then returns it cached. */
  getData: () => Promise<LinkedData | null>;
  isLoading: boolean;
};

export function useLinkedConversationData(
  parentConversation: number | null,
): LinkedConversationData {
  const skip = !parentConversation;

  const { data: convData, isFetching: convFetching } =
    conversationsApi.useReadConversationQuery(
      { csid: parentConversation! },
      { skip },
    );

  const rewrittenHtmlMessages = useAppSelector(
    (state) =>
      state.emailMessages.messages.filter((m) => m.csid === parentConversation),
    shallowEqual,
  );

  const getData = useMemo(() => {
    let cached: Promise<LinkedData | null> | undefined;

    return () => {
      if (cached === undefined) {
        cached = buildLinkedData(
          parentConversation,
          convData,
          rewrittenHtmlMessages,
        );
      }
      return cached;
    };
  }, [parentConversation, convData, rewrittenHtmlMessages]);

  return { getData, isLoading: !skip && convFetching };
}

async function buildLinkedData(
  parentConversation: number | null,
  convData: ReadConversationApiResponse | undefined,
  rewrittenHtmlMessages: EmailMessageType[],
): Promise<LinkedData | null> {
  if (!parentConversation || !convData) {
    return null;
  }

  const mainConversation = convData.data.history.main_conversation;

  if (mainConversation.id !== parentConversation) {
    return null;
  }

  const { events, authors } = mainConversation;
  const messages = events.map(
    (event) =>
      ({
        ...event,
        author: authors[(event as any).author_id] ?? null,
        text: (event as any).text ?? '',
        cc_list_v2: (event as any).cc_list ?? null,
        bcc_list_v2: (event as any).bcc_list ?? null,
      }) as unknown as Message,
  );

  const metaConversation = {
    conversation: mainConversation,
    messages,
  } as unknown as MetaConversation;

  const {
    transcript,
    attachments,
    authors: transcriptAuthors,
  } = await getMessagesTranscript(metaConversation, rewrittenHtmlMessages);
  const subject = mainConversation.attributes?.subject?.trim() ?? '';
  const lead = getTranscriptLead(
    mainConversation as unknown as Conversation,
    transcriptAuthors,
    messages,
  );

  return {
    body: transcript,
    subject,
    forwardBody: `${lead}${transcript}`,
    forwardSubject: subject ? `Fwd: ${subject}` : '',
    attachments: attachments.map((a) => ({
      url: a.relative_uri,
      prettyName: a.name,
      absoluteUri: a.absolute_uri,
      size: 0,
    })),
  };
}
